import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Paperclip, Send, ExternalLink } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import VendorLayout from "@/components/vendor_portal/VendorLayout";

const VENDOR_STATUSES = [
  "Approved", "Ordered", "Backordered", "Waiting on Vendor", "Receiving",
];

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">{label}</div>
      <div className="text-sm text-[#1A1A1A] mt-0.5">{value || <span className="text-[#a8a8a8]">—</span>}</div>
    </div>
  );
}

function LineItemRow({ orderId, item, assigned, onUpdated }) {
  const [selected, setSelected] = useState(item.status_v2 || "Pending");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { setSelected(item.status_v2 || "Pending"); }, [item.status_v2]);

  const apply = async () => {
    if (!selected || selected === item.status_v2) return;
    setSaving(true); setError("");
    try {
      const { data } = await api.patch(
        `/vendor/requests/${orderId}/items/${item.line_id}/status`,
        { status: selected, note: note.trim() || undefined },
      );
      onUpdated?.(data);
      setNote("");
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Update failed.");
    } finally { setSaving(false); }
  };

  const specs = [
    ["Brand", item.brand], ["Style #", item.style_number],
    ["Color", item.color], ["Size", item.size], ["Qty", item.quantity],
    ["Logo", item.logo_preference], ["Location", item.logo_location],
    ["Logo Color", item.logo_color], ["Thread", item.thread_color],
  ];

  return (
    <div className={`p-4 border ${assigned ? "border-[#B59A5A] bg-[#FFFCEF]" : "border-[#E5E3D8] bg-white opacity-60"}`}
         data-testid={`vendor-line-item-${item.line_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">
            {assigned ? "Assigned to you" : "Assigned to another vendor"}
            <span className="ml-2 text-[#7A7A7A] normal-case tracking-normal">{item.line_id}</span>
          </div>
          <div className="font-serif text-lg text-[#1A1A1A] mt-0.5">{item.item_name || "(unnamed)"}</div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#1A422D]">
          {item.status_v2 || "Pending"}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {specs.map(([k, v]) => <KV key={k} label={k} value={v ? String(v) : ""} />)}
      </div>

      {assigned && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <select value={selected} onChange={(e) => setSelected(e.target.value)}
                  data-testid={`vendor-status-select-${item.line_id}`}
                  className="input-field text-sm max-w-[200px]">
            {VENDOR_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input value={note} onChange={(e) => setNote(e.target.value)}
                 placeholder="Note to pro shop (optional)"
                 data-testid={`vendor-status-note-${item.line_id}`}
                 className="input-field text-sm flex-1 min-w-[180px]" />
          <button type="button" onClick={apply}
                  disabled={saving || selected === item.status_v2}
                  data-testid={`vendor-status-apply-${item.line_id}`}
                  className="btn-primary text-xs">
            {saving ? "Updating…" : "Update Status"}
          </button>
        </div>
      )}
      {error && <div className="mt-2 text-xs text-[#a33]">{error}</div>}
    </div>
  );
}

function MessageBubble({ msg }) {
  const isVendor = msg.author_role === "vendor";
  return (
    <div className={`flex ${isVendor ? "justify-end" : "justify-start"}`} data-testid={`msg-${msg.message_id}`}>
      <div className={`max-w-[75%] px-4 py-3 border ${isVendor ? "bg-[#F9F8F4] border-[#B59A5A]" : "bg-white border-[#E5E3D8]"}`}>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#8A6D2E]">
          {msg.author_role === "vendor" ? "You" : "Pro Shop"} · {msg.author_name || ""}
        </div>
        <div className="mt-1 text-sm text-[#1A1A1A] whitespace-pre-wrap">{msg.body}</div>
        {msg.attachments?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {msg.attachments.map((a) => (
              <a key={a.attachment_id}
                 href={`${process.env.REACT_APP_BACKEND_URL}/api/message-attachments/${msg.message_id}/${a.attachment_id}`}
                 target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 text-xs text-[#1A422D] underline">
                <Paperclip size={11} /> {a.original_filename || "attachment"}
              </a>
            ))}
          </div>
        )}
        <div className="mt-1 text-[10px] text-[#7A7A7A]">{new Date(msg.created_at).toLocaleString()}</div>
      </div>
    </div>
  );
}

function VendorMessagesThread({ orderId }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/vendor/requests/${orderId}/messages`);
      setMessages(data || []);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "");
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true); setError("");
    try {
      const fd = new FormData();
      fd.append("body", body.trim());
      for (const f of files) fd.append("files", f);
      await api.post(`/vendor/requests/${orderId}/messages`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBody(""); setFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Send failed.");
    } finally { setSending(false); }
  };

  return (
    <div className="mt-8 p-6 bg-white border border-[#E5E3D8]" data-testid="vendor-thread">
      <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">
        Direct Thread with Pro Shop
      </div>
      <div className="mt-4 space-y-3 max-h-[400px] overflow-y-auto">
        {messages.length === 0 && <div className="text-sm text-[#7A7A7A]">No messages yet. Say hello or attach an artwork proof.</div>}
        {messages.map((m) => <MessageBubble key={m.message_id} msg={m} />)}
      </div>
      <div className="mt-4 border-t border-[#F0EDE0] pt-4">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3}
                  placeholder="Message the pro shop — questions, confirmations, ship dates, etc."
                  data-testid="vendor-msg-body" className="input-field" />
        <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => fileRef.current?.click()}
                    data-testid="vendor-msg-attach"
                    className="inline-flex items-center gap-1 text-xs text-[#1A422D] hover:text-[#B59A5A]">
              <Paperclip size={13} /> Attach files
            </button>
            <input ref={fileRef} type="file" multiple hidden
                   accept="image/*,application/pdf"
                   onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            {files.length > 0 && <span className="text-xs text-[#7A7A7A]">{files.length} file(s) queued</span>}
          </div>
          <button type="button" onClick={send} disabled={sending || !body.trim()}
                  data-testid="vendor-msg-send"
                  className="btn-primary text-xs inline-flex items-center gap-1">
            <Send size={12} /> {sending ? "Sending…" : "Send"}
          </button>
        </div>
        {error && <div className="mt-2 text-xs text-[#a33]">{error}</div>}
      </div>
    </div>
  );
}

export default function VendorRequestDetailPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get(`/vendor/requests/${id}`)
      .then(({ data }) => setOrder(data))
      .catch((err) => setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || ""));
  }, [id]);

  const assignedSet = useMemo(() => new Set(order?._assigned_line_ids || []), [order]);
  const openPacket = async () => {
    setDownloading(true);
    try {
      const resp = await api.get(`/vendor/requests/${id}/vendor-packet.pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 20000);
    } catch (err) {
      // Vendor route auth may 403 for the admin route — fall back to a
      // future dedicated vendor-facing packet endpoint. For now log.
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Packet download not authorised.");
    } finally { setDownloading(false); }
  };

  if (error && !order) {
    return (
      <VendorLayout>
        <section>
          <Link to="/vendor/requests" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#7A7A7A] hover:text-[#1A422D]">
            <ArrowLeft size={13} /> Back to Requests
          </Link>
          <div className="mt-6 p-6 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">{error}</div>
        </section>
      </VendorLayout>
    );
  }
  if (!order) return <VendorLayout><div className="text-sm text-[#7A7A7A]">Loading…</div></VendorLayout>;

  const items = order.items || [];
  const anyAssigned = items.some((it) => assignedSet.has(it.line_id));

  return (
    <VendorLayout>
      <section data-testid="vendor-request-detail">
        <Link to="/vendor/requests" data-testid="vendor-req-back"
              className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#7A7A7A] hover:text-[#1A422D]">
          <ArrowLeft size={13} /> Back to Requests
        </Link>
        <div className="mt-3 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[12px] uppercase tracking-[0.28em] font-semibold text-[#B59A5A]">Vendor Request</div>
            <h1 className="font-serif text-4xl text-[#1A422D] mt-2">{order.display_order_id || order.order_id}</h1>
            <div className="text-sm text-[#7A7A7A] mt-1">{order.club_name || ""}</div>
          </div>
          <button type="button" onClick={openPacket} disabled={downloading}
                  data-testid="vendor-req-packet-open"
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold border border-[#1A422D] text-[#1A422D] hover:bg-[#E9F0EB]">
            <Download size={13} /> {downloading ? "Opening…" : "Open Vendor Packet PDF"}
          </button>
        </div>

        <div className="mt-6 p-6 bg-white border border-[#E5E3D8]">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KV label="Overall Status" value={order.rolled_up_status || order.status} />
            <KV label="Submitted" value={order.created_at ? new Date(order.created_at).toLocaleDateString() : ""} />
            <KV label="Requested Delivery" value={order.requested_delivery_date} />
            <KV label="Items Assigned" value={anyAssigned ? `${assignedSet.size || items.length} of ${items.length}` : "Full order"} />
          </div>
          {order.vendor_notes && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">Notes from Pro Shop</div>
              <div className="mt-1 text-sm text-[#1A1A1A] whitespace-pre-wrap">{order.vendor_notes}</div>
            </div>
          )}
        </div>

        <div className="mt-6 space-y-3">
          {items.map((it) => (
            <LineItemRow
              key={it.line_id}
              orderId={order.order_id}
              item={it}
              assigned={assignedSet.size === 0 || assignedSet.has(it.line_id)}
              onUpdated={setOrder}
            />
          ))}
        </div>

        <VendorMessagesThread orderId={order.order_id} />
      </section>
    </VendorLayout>
  );
}
